import moment from 'moment-timezone';
import { IVolunteerShift } from '../../../queries/Volunteer';

export const resolveZone = (zone?: string): string => zone || moment.tz.guess();

export const composeShiftInstant = (date: string, time: string, zone?: string): string =>
	moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', resolveZone(zone)).toISOString();

export const formatShiftTimeRange = (shift: Pick<IVolunteerShift, 'startAt' | 'endAt'>, zone?: string): string => {
	if (!shift.startAt) {
		return '';
	}
	const z = resolveZone(zone);
	const start = moment.tz(shift.startAt, z);
	const abbr = start.format('z');
	if (!shift.endAt) {
		return `${start.format('h:mm A')} ${abbr}`;
	}
	const end = moment.tz(shift.endAt, z);
	return `${start.format('h:mm A')} – ${end.format('h:mm A')} ${abbr}`;
};

export const shiftOptionLabel = (shift: IVolunteerShift, zone?: string): string => {
	if (!shift.startAt) {
		return [shift.description, shift.location].filter(Boolean).join(' · ') || 'Unscheduled shift';
	}
	const day = moment.tz(shift.startAt, resolveZone(zone)).format('ddd, MMM D');
	return [day, formatShiftTimeRange(shift, zone), shift.location].filter(Boolean).join(' · ');
};

export interface ITimeSlot {
	start: string;
	end: string;
}

export const sliceTimeWindow = (startTime: string, endTime: string, durationMinutes: number): ITimeSlot[] => {
	const start = moment(startTime, 'HH:mm', true);
	const end = moment(endTime, 'HH:mm', true);
	if (!start.isValid() || !end.isValid() || !end.isAfter(start) || durationMinutes <= 0) {
		return [];
	}
	const slots: ITimeSlot[] = [];
	const cursor = start.clone();
	while (!cursor.clone().add(durationMinutes, 'minutes').isAfter(end)) {
		const slotEnd = cursor.clone().add(durationMinutes, 'minutes');
		slots.push({ start: cursor.format('HH:mm'), end: slotEnd.format('HH:mm') });
		cursor.add(durationMinutes, 'minutes');
	}
	return slots;
};

export interface IShiftDayGroup {
	dayKey: string;
	dayLabel: string;
	shifts: IVolunteerShift[];
}

export const groupShiftsByDay = (shifts: IVolunteerShift[], zone?: string): IShiftDayGroup[] => {
	const z = resolveZone(zone);
	const sorted = [...shifts]
		.filter(shift => !!shift.startAt)
		.sort((a, b) => (a.startAt ?? '').localeCompare(b.startAt ?? ''));
	const groups: IShiftDayGroup[] = [];
	const byKey = new Map<string, IShiftDayGroup>();
	for (const shift of sorted) {
		const local = moment.tz(shift.startAt as string, z);
		const dayKey = local.format('YYYY-MM-DD');
		let group = byKey.get(dayKey);
		if (!group) {
			group = { dayKey, dayLabel: local.format('dddd, MMM D, YYYY'), shifts: [] };
			byKey.set(dayKey, group);
			groups.push(group);
		}
		group.shifts.push(shift);
	}
	return groups;
};
